import * as React from "react";
import { isPossiblePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import { ErrorType } from "lib/types/address";
import {
  isValidEmail,
  isIndianNumber,
  containsEmojis,
  isNumeric,
  regexPatterns,
} from "lib/utils/validations";
import { FormatterTypes, LengthLimitType, ValidationItem } from "lib/types/validation";
import { areObjectsEqual, capitalizeFirstCharacter } from "lib/utils/helpers";
import { useLocale } from "lib/hooks/useLocale";
import { usePrevious } from "lib/hooks/usePrevious";
import {
  applyValueFormatters,
  validateFieldAgainstSchema,
  validateSelectedAddress,
} from "lib/utils/address-validation";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

export type ValidationSchema = Record<FormField, ValidationItem>;

export enum FormField {
  // common login validation
  phoneNumber = "phoneNumber",
  otp = "otp",
  // address validation
  phone = "phone",
  alternate_number = "alternate_number",
  id = "id",
  email = "email",
  name = "name",
  address1 = "address1",
  address2 = "address2",
  zip = "zip",
  city = "city",
  state = "state",
  state_code = "state_code",
  country = "country",
  country_code = "country_code",
  type = "type",
  // business validation
  gstin = "gstin",
  gst_business_name = "gst_business_name",
  // Card validation
  cardName = "cardName",
  cardNumber = "cardNumber",
  expiry = "expiry",
  cvv = "cvv",
}

interface UseFormProps<T> {
  initialState: T;
  validationSchema: Partial<ValidationSchema>;
  skipValidation?: boolean;
  initialErrorState?: Record<string, ErrorType | undefined>;
}

export default function useForm<T extends Record<string, unknown>>({
  initialState,
  validationSchema,
  skipValidation = false,
  initialErrorState,
}: UseFormProps<T>) {
  const [values, setValues] = React.useState(initialState);
  const [errors, setErrors] = React.useState<Record<string, ErrorType | undefined>>(
    initialErrorState ? initialErrorState : {},
  );
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [validations, setValidations] = React.useState<Record<string, boolean>>({});
  const [fieldsValidated, setFieldsValidated] = React.useState<boolean>(false);
  const [shouldScroll, setShouldScroll] = React.useState<boolean>(true);

  const { t } = useLocale();
  const {
    state: { addressConfig },
  } = useMerchantContext();

  React.useEffect(() => {
    if (!fieldsValidated && !areObjectsEqual(values, initialState)) {
      setFieldsValidated(true);
      validateAutoFilledFields();
    }
  }, [values]);

  React.useEffect(() => {
    validateAutoFilledFields();
  }, []);

  const validateAutoFilledFields = () => {
    Object?.keys(values).map((key) => {
      if (values[key]) {
        setValidations((vlds) => ({ ...vlds, [key]: true }));
      }
    });
  };

  const setValueOf = (field: string, value: unknown) => {
    setValues((values: any) => ({ ...values, [field]: value }));
  };

  const validate = (values: any) => {
    const { available, errorFieldData } = validateSelectedAddress({
      selectedAddress: values,
      addressConfig,
      validationSchema,
      t,
    });

    if (!available) {
      setErrors(errorFieldData);
    }

    return available;
  };

  function scrollErrorsIntoView() {
    let scrollTo = "";
    for (let item of Object.values(FormField)) {
      if (errors[item] && errors[item]?.status) {
        scrollTo = item;
        break;
      }
    }

    document.getElementById(`flo-field-${scrollTo}`)?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });

    setShouldScroll(false);
  }

  const previousError = usePrevious(errors);

  React.useEffect(() => {
    if (previousError !== errors && shouldScroll) {
      scrollErrorsIntoView();
    }
  }, [errors]);

  function handleSubmit(callback: (values: any) => void, options = { shouldScrollErrorsIntoView: true }) {
    return function (event: any) {
      event.preventDefault();
      const isValid = validate(values);
      setShouldScroll(true);
      if (!isValid) {
        return;
      }
      callback(values);
    };
  }

  const handleFieldChange = (fieldName: FormField, value: unknown) => {
    setTouched((touched) => ({ ...touched, [fieldName]: true }));
    setValidations((vlds) => ({ ...vlds, [fieldName]: false }));
    const fieldSchema = validationSchema[fieldName];
    value = applyValueFormatters({ value, formatters: ["CLEAN_EMOJIS"] });
    if (fieldSchema?.formatters) {
      value = applyValueFormatters({ value, formatters: fieldSchema.formatters });
    }
    if (errors[fieldName] && !validateFieldAgainstSchema({ value, fieldSchema, addressFields: values, t })) {
      setErrors((errs) => ({ ...errs, [fieldName]: undefined }));
    }
    setValueOf(fieldName, value);
  };

  const inputProps = (
    fieldName: FormField,
    config?: {
      onChange?: (e: any) => void;
      onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
      onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
    },
  ) => {
    const hasCustomOnChange = config?.onChange != null;

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;
      handleFieldChange(fieldName, value);
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
      if (config?.onBlur != null) {
        config.onBlur(event);
      }

      if (!touched[fieldName]) {
        return;
      }

      const fieldSchema = validationSchema[fieldName];

      const error = validateFieldAgainstSchema({
        value: values[fieldName],
        fieldSchema,
        addressFields: values,
        t,
      });

      if (error) {
        setErrors((errs) => ({ ...errs, [fieldName]: error }));
      } else if (errors[fieldName]) {
        setErrors((errs) => ({ ...errs, [fieldName]: undefined }));
      } else {
        setValidations((vlds) => ({ ...vlds, [fieldName]: true }));
      }
    };

    const onFocus = (event: React.FocusEvent<HTMLElement>): void => {
      if (config?.onFocus != null) {
        config.onFocus(event);
      }
    };

    return {
      name: fieldName,
      id: fieldName,
      onChange: (hasCustomOnChange ? config.onChange : onChange) as (e: any) => void,
      onBlur,
      onFocus,
      value: values[fieldName] as string,
    };
  };

  return {
    handleSubmit,
    setValues,
    setErrors,
    setValueOf,
    handleFieldChange,
    state: {
      values,
      errors,
      validations,
    },
    inputProps,
  };
}
